import React from "react";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { motion } from "motion/react";
import { animated } from "@react-spring/web";
import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { useLocalizedContent } from "../context/LanguageContext";
import { useParallax } from "../hooks/useParallax";
import { useTilt } from "../hooks/useTilt";
import SectionParallax from "./SectionParallax";

const sectionVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.12, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 140, damping: 18 },
  },
};

const Availability = () => {
  const { availability, personalInfo } = useLocalizedContent();
  const contentShift = useParallax(0.02);
  const photoTilt = useTilt({ max: 10, scale: 1.02 });
  const [imageLoaded, setImageLoaded] = React.useState(false);
  const [imageError, setImageError] = React.useState(false);

  return (
    <motion.section
      id="availability"
      className="section-fade relative py-24 overflow-hidden"
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black via-zinc-900 to-black"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_right,_var(--tw-gradient-stops))] from-blue-900/15 via-transparent to-transparent"></div>
      <SectionParallax variant="blue" />

      <div
        className="relative z-10 container mx-auto px-6 lg:px-12 transition-transform duration-500"
        style={{ transform: `translate3d(0, ${contentShift}px, 0)` }}
      >
        <motion.div variants={sectionVariants}>
          <div className="grid lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-10 items-center">
            <animated.div
              ref={photoTilt.ref}
              style={photoTilt.style}
              {...photoTilt.bind}
              className="will-change-transform"
            >
              <Card className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/5 backdrop-blur-xl p-6 sm:p-8">
                <motion.div
                  className="absolute -top-8 -right-8 h-28 w-28 rounded-full border border-cyan-400/30"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
                >
                  <div className="absolute inset-3 rounded-full border border-blue-400/20"></div>
                </motion.div>
                <div className="absolute -bottom-16 -left-16 h-40 w-40 rounded-full bg-cyan-500/15 blur-3xl"></div>

                <div className="relative z-10">
                  <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-black/30">
                    <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/15 via-transparent to-cyan-500/20"></div>
                    <img
                      src={personalInfo.portrait}
                      alt={availability.imageAlt}
                      loading="lazy"
                      decoding="async"
                      onLoad={() => setImageLoaded(true)}
                      onError={() => setImageError(true)}
                      className={`h-[420px] w-full object-cover transition-opacity duration-500 sm:h-[480px] ${
                        imageLoaded && !imageError ? "opacity-100" : "opacity-0"
                      }`}
                    />

                    {!imageLoaded && !imageError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/40 backdrop-blur">
                        <Skeleton className="h-20 w-20 rounded-full bg-white/10" />
                        <span className="text-xs uppercase tracking-wide text-white/70">
                          {availability.loadingImage}
                        </span>
                      </div>
                    )}

                    {imageError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/60 text-white/70">
                        <span className="text-sm font-semibold">
                          {availability.imageUnavailableTitle}
                        </span>
                        <span className="text-xs">
                          {availability.imageUnavailableSubtitle}
                        </span>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                    <div className="absolute bottom-5 left-5 right-5">
                      <div className="rounded-2xl border border-white/20 bg-white/10 backdrop-blur-xl p-4">
                        <div className="flex items-center gap-2 text-sm text-white/80">
                          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]"></span>
                          <span>{availability.status.label}</span>
                        </div>
                        <p className="mt-1 text-xs text-white/55">
                          {availability.status.note}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-2">
                    {availability.highlights.map((item) => (
                      <span
                        key={item}
                        className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/70"
                      >
                        <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </Card>
            </animated.div>

            <div className="space-y-8">
              <motion.div className="space-y-4" variants={itemVariants}>
                <span className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-4 py-1.5 text-xs font-medium uppercase tracking-wider text-blue-200">
                  {availability.badge}
                </span>
                <p className="text-[11px] uppercase tracking-[0.45em] text-white/40">
                  {availability.kicker}
                </p>
                <h2 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                  {availability.heading}
                </h2>
                <p className="text-lg text-white/55 leading-relaxed max-w-xl">
                  {availability.description}
                </p>
              </motion.div>

              <motion.div
                className="grid gap-4 sm:grid-cols-3"
                variants={itemVariants}
              >
                {availability.stats.map((stat) => (
                  <div
                    key={stat.label}
                    className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-md px-4 py-5 text-center"
                  >
                    <div className="text-2xl font-semibold text-blue-200">
                      {stat.value}
                    </div>
                    <div className="mt-1 text-xs uppercase tracking-wider text-white/50">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>

              <motion.div className="flex flex-wrap gap-4" variants={itemVariants}>
                <motion.a
                  href="#contact"
                  whileHover={{ y: -2, scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="group inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-400 to-cyan-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/40 hover:shadow-blue-500/70"
                >
                  {availability.ctaPrimary}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </motion.a>
                <motion.a
                  href="#projects"
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white/70 hover:text-white hover:border-white/30 hover:bg-white/10"
                >
                  {availability.ctaSecondary}
                </motion.a>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default Availability;
